import { useState } from "react";
import { Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const CONFIRM_PHRASE = "DELETE DEMO DATA";

export default function MaintenanceSettings() {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/cleanup-demo-data", { confirm });
      setResult(data.data);
      setConfirm("");
      const total = Object.entries(data.data)
        .filter(([k]) => k !== "admin_created")
        .reduce((s, [, v]) => s + (Number(v) || 0), 0);
      toast.success(`Demo data cleaned — ${total} record(s) removed`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-red-200 bg-red-50/40 p-6" data-testid="maintenance-settings">
      <h2 className="mb-1 flex items-center gap-2 font-sora font-semibold text-red-700">
        <AlertTriangle className="h-5 w-5" />Danger zone
      </h2>
      <p className="mb-4 text-sm text-ink/70">
        Remove all demo/seed data — sample leads (<code className="text-xs">@example.com</code>), demo
        accounts (<code className="text-xs">@acquireloop.com</code>), and seeded routing rules. Your real
        leads, your accounts, and settings are <strong>not</strong> affected. This cannot be undone.
      </p>
      <label className="block max-w-sm">
        <span className="mb-1 block text-xs font-medium text-ink/70">Type <strong>{CONFIRM_PHRASE}</strong> to confirm</span>
        <input
          data-testid="cleanup-confirm-input"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={CONFIRM_PHRASE}
          className="h-10 w-full rounded-lg border border-red-200 px-3 text-sm outline-none focus:border-red-400"
        />
      </label>
      <button
        onClick={run}
        disabled={busy || confirm.trim() !== CONFIRM_PHRASE}
        data-testid="cleanup-demo-data-btn"
        className="mt-4 flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        Clean up demo data
      </button>
      {result && (
        <p className="mt-3 text-xs text-ink/60" data-testid="cleanup-result">
          Removed: {result.leads || 0} leads, {result.users || 0} demo users, {result.assignment_rules || 0} rules
          {result.admin_created ? " · admin re-created" : ""}.
        </p>
      )}
    </div>
  );
}
